import { ChangeDetectionStrategy, Component, output, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-aula4-section',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8">
      <!-- Breadcrumb Navigation & Top Action Bar -->
      <div class="flex flex-wrap items-center justify-between gap-4">
        <button 
          type="button" 
          (click)="navigateBack.emit()" 
          class="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-semibold text-[#F27D26] border border-white/10 transition-all cursor-pointer">
          <span class="material-symbols-outlined text-base">arrow_back</span>
          <span>Voltar ao Menu de Aulas</span>
        </button>

        <div class="flex flex-wrap items-center gap-3">
          <!-- View Switcher Tabs -->
          <div class="flex items-center space-x-1 bg-[#121214] p-1.5 rounded-xl border border-white/10">
            <button 
              type="button"
              (click)="onSelectView('slides')"
              [class]="activeView() === 'slides' 
                ? 'bg-[#F27D26] text-black font-bold shadow-md shadow-[#F27D26]/20' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'"
              class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">slideshow</span>
              <span>Slides (11 Slides)</span>
            </button>

            <button 
              type="button"
              (click)="onSelectView('activity')"
              [class]="activeView() === 'activity' 
                ? 'bg-[#F27D26] text-black font-bold shadow-md shadow-[#F27D26]/20' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'"
              class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">assignment</span>
              <span>Folha de Atividades Completa</span>
            </button>
          </div>
        </div>
      </div>

      <!-- VIEW 1: INLINE SLIDES PRESENTATION CARD -->
      @if (activeView() === 'slides') {
        <div class="bg-[#121214] rounded-2xl border border-white/10 shadow-2xl p-6 sm:p-10 min-h-[580px] flex flex-col justify-between relative overflow-hidden">
          <!-- Slide Glow Accent -->
          <div class="absolute -top-24 -right-24 w-80 h-80 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>

          <!-- Top Banner Bar -->
          <div class="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-white/10">
            <div class="flex items-center space-x-2">
              <span class="material-symbols-outlined text-[#F27D26] text-xl">slideshow</span>
              <span class="text-xs font-mono font-semibold text-zinc-300">Modo de Leitura Integrado</span>
            </div>
            
            <button 
              type="button" 
              (click)="openPresentationModal()" 
              class="px-3 py-1.5 rounded-lg bg-[#F27D26]/15 hover:bg-[#F27D26]/25 text-[#F27D26] border border-[#F27D26]/30 text-xs font-bold transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-sm">present_to_all</span>
              <span>Apresentação Imersiva</span>
            </button>
          </div>

          <!-- SLIDES CONTENT -->
          <div class="my-6">
            <!-- SLIDE 1 -->
            @if (currentSlide() === 0) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">storefront</span>
                  <span>AULA 4 • TIPOS DE EMPREENDEDORISMO & MERCADO</span>
                </span>

                <div>
                  <h1 class="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                    Quem Você Quer Ser no Mercado de Redes?
                  </h1>
                  <p class="text-lg text-zinc-400 font-medium mt-1">
                    Classificação dos modelos de empreendedorismo e posicionamento estratégico em TIC.
                  </p>
                </div>

                <div class="bg-[#0A0A0B] rounded-xl p-6 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3">
                  <h3 class="text-lg font-bold text-white flex items-center space-x-2">
                    <span>🙋‍♂️</span>
                    <span>A Encruzilhada do João (Fim do Ano se Aproximando):</span>
                  </h3>
                  <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                    O João está concluindo o curso técnico em Redes. Com a experiência adquirida no projeto da <em>Logística Expressa</em>, surgiram vários caminhos: prestar serviços informais para o comércio local, abrir um MEI formal para emitir notas fiscais, prestar consultoria remota em nuvem ou aceitar uma vaga CLT como analista de suporte em um grande Provedor de Internet (ISP).
                  </p>
                  <p class="text-zinc-400 text-xs italic text-justify">
                    Ele percebeu que existem muitas formas de empreender além do modelo tradicional. Qual é a melhor escolha para o início de carreira?
                  </p>
                </div>
              </div>
            }

            <!-- SLIDE 2 -->
            @if (currentSlide() === 1) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">analytics</span>
                  <span>PASSO 1 • PANORAMA DE MERCADO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Mitos e Verdades sobre Empreender
                </h2>
                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  Segundo dados e análises da <strong>Serasa Experian</strong>, empreendedorismo não se resume a abrir grandes empresas ou investir milhões:
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-zinc-500 border-y border-r border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-zinc-300 flex items-center space-x-2">
                      <span class="material-symbols-outlined text-zinc-400">domain_disabled</span>
                      <span>🏢 A Visão Tradicional (Engessada)</span>
                    </h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Acreditar que só empreende quem tem muito dinheiro para alugar escritório, comprar equipamentos caros e contratar dezenas de funcionários.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-[#F27D26] flex items-center space-x-2">
                      <span class="material-symbols-outlined text-[#F27D26]">rocket_launch</span>
                      <span>🚀 A Realidade do Mercado Atual</span>
                    </h3>
                    <p class="text-zinc-300 text-xs leading-relaxed text-justify">
                      Empreender é a <strong>capacidade de identificar problemas, propor soluções inovadoras e gerar valor sustentável</strong> — seja de forma individual, digital, cooperativa ou dentro de uma grande corporação.
                    </p>
                  </div>
                </div>

                <div class="bg-[#F27D26]/10 border border-[#F27D26]/30 rounded-xl p-4 text-center">
                  <span class="text-xs sm:text-sm font-mono font-bold text-[#F27D26]">
                    NO BRASIL, MAIS DE 70% DAS EMPRESAS ATIVAS SÃO FORMADAS POR MICROEMPREENDEDORES INDIVIDUAIS (MEI) E PEQUENOS NEGÓCIOS!
                  </span>
                </div>
              </div>
            }

            <!-- SLIDE 3 -->
            @if (currentSlide() === 2) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">explore</span>
                  <span>PASSO 2 • MOTIVAÇÃO INICIAL</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Empreendedorismo por Oportunidade vs. Necessidade
                </h2>
                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  A origem de qualquer iniciativa empreendedora parte de uma destas duas realidades:
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-amber-500 border-y border-r border-white/10 space-y-2">
                    <span class="px-2 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-400 font-bold uppercase font-mono">TIPO A</span>
                    <h3 class="text-base font-bold text-amber-400">⚡ Por Necessidade (Reativo)</h3>
                    <ul class="text-zinc-300 text-xs space-y-1.5 list-disc pl-4 text-justify">
                      <li>Surge pela falta de alternativas no mercado de trabalho formal ou desemprego.</li>
                      <li>Pouco ou nenhum planejamento prévio; foco em pagar as contas do mês.</li>
                      <li><strong class="text-amber-300">Ex em TIC:</strong> Passar cabos ou formatar computadores para vizinhos cobrando qualquer valor para gerar renda emergencial.</li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-2">
                    <span class="px-2 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-400 font-bold uppercase font-mono">TIPO B</span>
                    <h3 class="text-base font-bold text-emerald-400">🎯 Por Oportunidade (Planejado)</h3>
                    <ul class="text-zinc-300 text-xs space-y-1.5 list-disc pl-4 text-justify">
                      <li>Surge da identificação consciente de uma carência ou falha no mercado.</li>
                      <li>Envolve pesquisa de dados, cálculo de margem e proposta de valor clara.</li>
                      <li><strong class="text-emerald-300">Ex em TIC:</strong> Notar que os comércios locais perdem vendas por falta de link backup e criar planos de redundância gerenciada.</li>
                    </ul>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 4 -->
            @if (currentSlide() === 3) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">category</span>
                  <span>PASSO 3 • MODELOS DE NEGÓCIO (PARTE 1)</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Modelos de Empreendedorismo em TIC (Parte 1)
                </h2>
                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  Conheça os formatos práticos destacados no mercado segundo a Serasa Experian:
                </p>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-white/10 space-y-2">
                    <div class="text-2xl">👤</div>
                    <h3 class="text-sm font-bold text-white">1. Individual (MEI / Autônomo)</h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      O profissional formaliza seu trabalho como Microempreendedor Individual para emitir Notas Fiscais e atender empresas como consultor ou prestador de serviços de redes.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-amber-500/30 space-y-2">
                    <div class="text-2xl">⚠️</div>
                    <h3 class="text-sm font-bold text-amber-400">2. Informal</h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Atuação sem registro formal ("bicos"). Embora comum no início, traz riscos: impede de fechar contratos corporativos com empresas médias que exigem emissão de nota.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-cyan-500/30 space-y-2">
                    <div class="text-2xl">🌐</div>
                    <h3 class="text-sm font-bold text-cyan-400">3. Digital (Remoto / Nuvem)</h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Prestação de suporte e gestão de infraestrutura 100% online (NOC em nuvem, configuração remota de roteadores MikroTik e servidores AWS/Azure para empresas de qualquer lugar).
                    </p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 5 -->
            @if (currentSlide() === 4) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">category</span>
                  <span>PASSO 4 • MODELOS DE NEGÓCIO (PARTE 2)</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Modelos de Empreendedorismo em TIC (Parte 2)
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-emerald-500/30 space-y-2">
                    <div class="text-2xl">🏢</div>
                    <h3 class="text-sm font-bold text-emerald-400">4. Corporativo (Intraempreendedorismo)</h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Empreender como funcionário dentro de uma empresa contratante, liderando melhorias, cortando desperdícios de links e criando novos serviços internos.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-purple-500/30 space-y-2">
                    <div class="text-2xl">🤝</div>
                    <h3 class="text-sm font-bold text-purple-400">5. Cooperativo / Parcerias</h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      União de técnicos autônomos ou pequenos provedores que compartilham custos de links dedicados (Redes Neutras / PTT) para competir com grandes multinacionais.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-teal-500/30 space-y-2">
                    <div class="text-2xl">🌱</div>
                    <h3 class="text-sm font-bold text-teal-400">6. Social</h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Iniciativas voltadas a resolver problemas comunitários, como projetos de inclusão digital que levam sinal de internet aberto para comunidades carentes e escolas rurais.
                    </p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 6 -->
            @if (currentSlide() === 5) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">badge</span>
                  <span>DESTAQUE • INTRAEMPREENDEDORISMO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Empreendendo com Carteira Assinada
                </h2>
                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  O <strong>Intraempreendedor</strong> é o profissional mais valorizado e promovido dentro das empresas de tecnologia:
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-rose-500 border-y border-r border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-rose-400">😴 O Funcionário Acomodado</h3>
                    <ul class="text-zinc-400 text-xs space-y-1.5 list-disc pl-4 text-justify">
                      <li>Faz estritamente o que pediram no contrato e nada além.</li>
                      <li>Identifica uma falha grave de segurança no servidor e não avisa: <em>"Não é meu setor."</em></li>
                      <li>Passa anos na mesma função reclamando do salário.</li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-emerald-400">🚀 O Intraempreendedor em Redes</h3>
                    <ul class="text-zinc-300 text-xs space-y-1.5 list-disc pl-4 text-justify">
                      <li>Automatiza tarefas repetitivas de backup com scripts em Linux/Python.</li>
                      <li>Propõe a reorganização de rotas BGP para economizar nos custos de tráfego do provedor.</li>
                      <li>Ganha destaque, assume cargos de liderança técnica e recebe bônus por inovação.</li>
                    </ul>
                  </div>
                </div>

                <div class="bg-indigo-950/40 border border-indigo-500/30 rounded-xl p-4 text-xs text-indigo-200 leading-relaxed text-justify">
                  💡 <strong>Cultura Open Organization (Caso Red Hat):</strong> Na Red Hat (gigante do Linux empresarial), colaboradores que sugerem e executam melhorias em infraestrutura têm voz ativa e crescem na liderança global da empresa.
                </div>
              </div>
            }

            <!-- SLIDE 7 -->
            @if (currentSlide() === 6) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">crisis_alert</span>
                  <span>PASSO 5 • POSICIONAMENTO E PRECIFICAÇÃO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  A Armadilha do Técnico "Faz-Tudo"
                </h2>
                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  Por que o profissional generalista vive preso na guerra de preços?
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-rose-500/40 space-y-3">
                    <h3 class="text-base font-bold text-rose-400">O Técnico Genérico ("Faz-Tudo")</h3>
                    <div class="bg-rose-950/30 border border-rose-500/30 p-2.5 rounded text-center text-xs font-mono text-rose-300">
                      "Formato PC, troco conector, arrumo impressora, passo cabo e mexo em celular."
                    </div>
                    <ul class="text-zinc-400 text-xs space-y-1.5 list-disc pl-4 text-justify">
                      <li>Disputa clientes exclusivamente pelo <strong>menor preço</strong>.</li>
                      <li>O cliente o enxerga como mão de obra comum e pede desconto constantemente.</li>
                      <li>Ganha pouco por chamado avulso e trabalha até a exaustão.</li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-emerald-500/40 space-y-3">
                    <h3 class="text-base font-bold text-emerald-400">O Especialista Posicionado</h3>
                    <div class="bg-emerald-950/30 border border-emerald-500/30 p-2.5 rounded text-center text-xs font-mono text-emerald-300">
                      "Especialista em Redundância de Links MikroTik e Cibersegurança para E-commerce."
                    </div>
                    <ul class="text-zinc-300 text-xs space-y-1.5 list-disc pl-4 text-justify">
                      <li>Disputa contratos corporativos pelo <strong>valor gerado e autoridade</strong>.</li>
                      <li>O cliente paga mensalidades previsíveis de R$ 600 a R$ 1.500 sem reclamar.</li>
                      <li>Torna-se referência regional no seu segmento.</li>
                    </ul>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 8 -->
            @if (currentSlide() === 7) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">target</span>
                  <span>ESTRATÉGIA • NICHOS DE MERCADO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Nichos Estratégicos para Especialização
                </h2>
                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  Em vez de tentar abraçar o mundo, foque onde há alta demanda e poucos especialistas:
                </p>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-cyan-500/30 space-y-2">
                    <span class="px-2 py-0.5 rounded text-[10px] bg-cyan-500/20 text-cyan-400 font-bold uppercase font-mono">NICHO 1</span>
                    <h3 class="text-sm font-bold text-white flex items-center space-x-1.5">
                      <span>🛡️ Cibersegurança & LGPD</span>
                    </h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Implantação de Firewalls (pfSense/Fortinet), VPNs seguras para Home Office e blindagem de dados contra vazamentos e ransomware.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-amber-500/30 space-y-2">
                    <span class="px-2 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-400 font-bold uppercase font-mono">NICHO 2</span>
                    <h3 class="text-sm font-bold text-white flex items-center space-x-1.5">
                      <span>📶 Wi-Fi Corporativo & Eventos</span>
                    </h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Projetos de alta densidade sem quedas de sinal para hotéis, escolas, coworkings e eventos corporativos com roaming fluido.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-emerald-500/30 space-y-2">
                    <span class="px-2 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-400 font-bold uppercase font-mono">NICHO 3</span>
                    <h3 class="text-sm font-bold text-white flex items-center space-x-1.5">
                      <span>🌾 Conectividade Rural & Agro (IoT)</span>
                    </h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Enlaces de longa distância via rádio e redes de sensores para galpões, fazendas e agroindústrias fora do alcance das operadoras.
                    </p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 9 -->
            @if (currentSlide() === 8) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">person_pin</span>
                  <span>CASO PRÁTICO • APLICAÇÃO REAL</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  O Posicionamento Estratégico do João
                </h2>

                <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-amber-500 border-y border-r border-white/10">
                  <p class="text-zinc-300 text-sm text-justify">
                    Analisando os tipos de empreendedorismo, o João tomou uma decisão estratégica para a sua formatura:
                  </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-[#F27D26]">1. Formalização MEI + Digital:</h3>
                    <p class="text-zinc-300 text-xs leading-relaxed text-justify">
                      Abriu seu <strong>MEI</strong> para emitir notas fiscais e montou um painel de monitoramento <strong>Digital em Nuvem</strong> (Zabbix/Telegram) para atender clientes sem precisar ficar preso fisicamente em um local.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-[#F27D26]">2. Declaração de Posicionamento Clara:</h3>
                    <p class="text-zinc-300 text-xs leading-relaxed italic text-justify">
                      "Ajudo empresas de comércio e logística a eliminarem o risco de ficarem sem emitir Notas Fiscais através de soluções de Link Duplo MikroTik e monitoramento 24/7."
                    </p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 10 -->
            @if (currentSlide() === 9) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">formula</span>
                  <span>MÉTODO PRÁTICO • SUA MARCA PROFISSIONAL</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Como Criar seu Posicionamento em 3 Passos
                </h2>
                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  Estruture seu perfil profissional de forma magnética para clientes ou empresas contratantes:
                </p>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-white/10 text-center space-y-2">
                    <div class="text-2xl">👥</div>
                    <h3 class="text-sm font-bold text-white">1. Para Quem? (Público)</h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Qual nicho ou segmento você atende? (Ex: Mercados, Provedores, Escritórios de Contabilidade, Clínicas).
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-[#F27D26] text-center space-y-2">
                    <div class="text-2xl">⚡</div>
                    <h3 class="text-sm font-bold text-[#F27D26]">2. Qual Dor? (Problema)</h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Qual prejuízo você evita? (Ex: Quedas de internet na hora da venda, perda de dados por falta de backup).
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-white/10 text-center space-y-2">
                    <div class="text-2xl">💎</div>
                    <h3 class="text-sm font-bold text-white">3. Qual Valor? (Resultado)</h3>
                    <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                      Qual o benefício final? (Ex: Operação contínua 100% protegida, tranquilidade do empresário).
                    </p>
                  </div>
                </div>

                <div class="bg-[#F27D26]/10 border border-[#F27D26]/40 p-4 rounded-xl text-center">
                  <p class="text-xs sm:text-sm font-bold text-[#F27D26] italic">
                    "Eu ajudo [Público-Alvo] a resolver [Dor Principal] através de [Sua Solução Especializada de Redes], garantindo [Resultado Final de Valor]."
                  </p>
                </div>
              </div>
            }

            <!-- SLIDE 11 -->
            @if (currentSlide() === 10) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">verified</span>
                  <span>SÍNTESE DA AULA</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  A Grande Lição do Dia
                </h2>

                <div class="bg-[#0A0A0B] p-8 rounded-2xl border border-[#F27D26]/40 text-center shadow-xl">
                  <h3 class="text-base sm:text-xl font-bold text-[#F27D26] leading-relaxed">
                    "Não importa se você atuará como MEI, consultor em nuvem ou intraempreendedor em um grande provedor.<br><br>
                    O que define o valor do seu trabalho em redes é a sua clareza de posicionamento e o tamanho do problema que você resolve."
                  </h3>
                </div>

                <div class="bg-[#0A0A0B] rounded-xl p-6 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-3">
                  <h3 class="text-base font-bold text-white flex items-center space-x-2">
                    <span>🚀</span>
                    <span>Próximo Passo:</span>
                  </h3>
                  <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                    Abra a <strong>Folha de Atividades</strong>, analise os tipos de empreendedorismo aplicados ao setor de redes e construa a Proposta de Posicionamento Profissional da sua equipe!
                  </p>
                  <div class="pt-2">
                    <button 
                      type="button" 
                      (click)="onSelectView('activity')" 
                      class="px-4 py-2 rounded-lg bg-[#F27D26] text-black font-bold text-xs hover:bg-[#e06c17] transition-all flex items-center space-x-1.5 cursor-pointer">
                      <span class="material-symbols-outlined text-base">assignment</span>
                      <span>Ir para Folha de Atividades Completa</span>
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- Slide Controls Footer -->
          <div class="pt-4 border-t border-white/10 flex items-center justify-between">
            <button 
              type="button" 
              (click)="prevSlide()" 
              [disabled]="currentSlide() === 0"
              class="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 text-xs font-semibold text-white border border-white/10 transition-all flex items-center space-x-1 cursor-pointer">
              <span class="material-symbols-outlined text-base">chevron_left</span>
              <span>Anterior</span>
            </button>

            <div class="flex items-center space-x-3">
              <span class="text-xs text-zinc-400 font-mono font-medium">
                Slide {{ currentSlide() + 1 }} de {{ totalSlides }}
              </span>
              <div class="w-24 sm:w-36 h-2 bg-white/10 rounded-full overflow-hidden hidden sm:block">
                <div 
                  class="h-full bg-[#F27D26] transition-all duration-300"
                  [style.width.%]="((currentSlide() + 1) / totalSlides) * 100">
                </div>
              </div>
            </div>

            <button 
              type="button" 
              (click)="nextSlide()" 
              [disabled]="currentSlide() === totalSlides - 1"
              class="px-4 py-2 rounded-xl bg-[#F27D26] hover:bg-[#e06c17] disabled:opacity-30 disabled:hover:bg-[#F27D26] text-xs font-bold text-black transition-all flex items-center space-x-1 cursor-pointer">
              <span>Próximo</span>
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      }

      <!-- VIEW 2: FULL INTERACTIVE ACTIVITY SHEET -->
      @if (activeView() === 'activity') {
        <div class="bg-[#121214] rounded-2xl border border-white/10 shadow-2xl p-6 sm:p-10 space-y-8">
          <!-- Header Bar with Print Button -->
          <div class="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-white/10">
            <div>
              <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider mb-2">
                <span class="material-symbols-outlined text-sm">edit_note</span>
                <span>MAPEAMENTO & POSICIONAMENTO ESTRATÉGICO</span>
              </span>
              <h1 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                📝 Folha de Atividades & Mapeamento de Carreira
              </h1>
            </div>

            <button 
              type="button" 
              (click)="printActivitySheet()" 
              class="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-all flex items-center space-x-2 cursor-pointer shadow-lg shadow-emerald-600/20">
              <span class="material-symbols-outlined text-base">print</span>
              <span>Imprimir / Salvar PDF</span>
            </button>
          </div>

          <!-- MÓDULO A -->
          <div class="space-y-4">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO A: Tipos de Empreendedorismo no Mercado de TIC (Serasa Experian)</span>
            </h2>

            <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
              <strong>1. Relacione cada modalidade de empreendedorismo à sua respectiva aplicação prática no ecossistema de redes e tecnologia:</strong><br>
              Opções: <strong class="text-[#F27D26]">[ IND ]</strong> Individual (MEI) | <strong class="text-emerald-400">[ INT ]</strong> Intraempreendedorismo | <strong class="text-cyan-400">[ DIG ]</strong> Digital | <strong class="text-teal-400">[ SOC ]</strong> Social | <strong class="text-purple-400">[ COO ]</strong> Cooperativo / Parcerias
            </p>

            <div class="overflow-x-auto">
              <table class="w-full text-xs text-left border-collapse border border-white/10 rounded-xl overflow-hidden">
                <thead>
                  <tr class="bg-[#0A0A0B] text-[#F27D26] font-mono border-b border-white/10">
                    <th class="p-3 border-r border-white/10 w-24 text-center">Tipo</th>
                    <th class="p-3">Situação Prática no Setor de Redes e Infraestrutura</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/10 text-zinc-300">
                  <tr class="hover:bg-white/5 transition-colors">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="answers.q1_1" placeholder="Ex: INT" class="w-16 bg-[#0A0A0B] border border-white/20 rounded px-2 py-1 text-center font-mono font-bold text-[#F27D26] uppercase focus:outline-none">
                    </td>
                    <td class="p-3 text-justify">
                      Um analista de redes contratado em um hospital desenvolve, por iniciativa própria, um script em Python que monitora switches e avisa a equipe médica no Telegram sobre oscilações antes que as UTIs percam conectividade.
                    </td>
                  </tr>
                  <tr class="hover:bg-white/5 transition-colors">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="answers.q1_2" placeholder="Ex: IND" class="w-16 bg-[#0A0A0B] border border-white/20 rounded px-2 py-1 text-center font-mono font-bold text-[#F27D26] uppercase focus:outline-none">
                    </td>
                    <td class="p-3 text-justify">
                      Um especialista em servidores Mikrotik/Linux formaliza seu registro como MEI para emitir Notas Fiscais e fechar contratos de manutenção preventiva com empresas de logística e farmácias.
                    </td>
                  </tr>
                  <tr class="hover:bg-white/5 transition-colors">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="answers.q1_3" placeholder="Ex: COO" class="w-16 bg-[#0A0A0B] border border-white/20 rounded px-2 py-1 text-center font-mono font-bold text-[#F27D26] uppercase focus:outline-none">
                    </td>
                    <td class="p-3 text-justify">
                      Um grupo de pequenos provedores de internet locais (ISPs) se une para contratar juntos uma grande rota de Fibra Óptica compartilhada (Rede Neutra), reduzindo os custos de link para todos.
                    </td>
                  </tr>
                  <tr class="hover:bg-white/5 transition-colors">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="answers.q1_4" placeholder="Ex: SOC" class="w-16 bg-[#0A0A0B] border border-white/20 rounded px-2 py-1 text-center font-mono font-bold text-[#F27D26] uppercase focus:outline-none">
                    </td>
                    <td class="p-3 text-justify">
                      Um profissional de TIC cria um projeto comunitário sem fins lucrativos para recondicionar roteadores doados e fornecer sinal de Wi-Fi gratuito para uma escola em área rural isolada.
                    </td>
                  </tr>
                  <tr class="hover:bg-white/5 transition-colors">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="answers.q1_5" placeholder="Ex: DIG" class="w-16 bg-[#0A0A0B] border border-white/20 rounded px-2 py-1 text-center font-mono font-bold text-[#F27D26] uppercase focus:outline-none">
                    </td>
                    <td class="p-3 text-justify">
                      Uma consultoria de segurança de redes presta serviços de configuração remota de Firewalls e VPNs em nuvem para empresas de todo o Brasil sem precisar se deslocar fisicamente.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="space-y-2 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                2. Análise Crítica (Oportunidade vs. Necessidade):
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                Explique por que empreendimentos iniciados <strong>por oportunidade</strong> (planejamento, pesquisa de dados e identificação de mercado) apresentam maior estabilidade e retorno financeiro do que aqueles iniciados puramente <strong>por necessidade</strong> (urgência financeira e improviso):
              </p>
              <textarea 
                rows="3" 
                [(ngModel)]="answers.q2" 
                placeholder="Escreva sua resposta aqui..." 
                class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
            </div>
          </div>

          <!-- MÓDULO B -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO B: Estudo de Caso de Intraempreendedorismo (Cultura Red Hat)</span>
            </h2>

            <div class="bg-[#0A0A0B] p-5 rounded-xl border border-white/10 space-y-3">
              <h3 class="text-sm font-bold text-[#F27D26] flex items-center space-x-1.5">
                <span class="material-symbols-outlined text-base">menu_book</span>
                <span>📄 Estudo de Caso: Inovação e Postura de Dono dentro das Organizações</span>
              </h3>
              <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                A <strong>Red Hat</strong>, líder global em soluções corporativas de código aberto (Linux), revolucionou o mercado corporativo com o modelo de <em>Open Organization</em>. Lá dentro, os técnicos e desenvolvedores são incentivados a agir com <strong>postura empreendedora</strong>: qualquer colaborador pode questionar rotinas ineficientes, propor automatizações em servidores e criar novas ferramentas internas sem barreiras burocráticas.
              </p>
              <p class="text-xs text-zinc-400 leading-relaxed text-justify">
                No mercado de redes, o intraempreendedor é o técnico que não se limita a "cumprir ordens", mas atua ativamente para evitar perdas financeiras e melhorar a qualidade do serviço prestado pela empresa contratante.
              </p>
            </div>

            <div class="space-y-2 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                3. Atitudes Práticas no NOC:
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                Imagine que você foi contratado com carteira assinada para trabalhar no NOC (Centro de Operações de Rede) de um provedor de internet. Cite <strong>duas atitudes práticas de intraempreendedorismo</strong> que você pode adotar para gerar economia de custos ou melhoria técnica para o provedor:
              </p>
              <textarea 
                rows="3" 
                [(ngModel)]="answers.q3" 
                placeholder="Ex: 1. Criar scripts de backup automatizados. 2. Otimizar rotas BGP..." 
                class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
            </div>
          </div>

          <!-- MÓDULO C -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO C: Posicionamento Estratégico (O "Faz-Tudo" vs. O Especialista)</span>
            </h2>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-[#0A0A0B] p-4 rounded-xl border border-rose-500/30 space-y-2">
                <h3 class="text-xs font-bold text-rose-400 uppercase tracking-wider">Técnico A: Generalista ("Faz-Tudo")</h3>
                <ul class="text-xs text-zinc-300 space-y-1.5 pl-4 list-disc text-justify">
                  <li><strong>Proposta:</strong> "Formato PC, troco tela de celular, arrumo impressora e passo cabo."</li>
                  <li><strong>Concorrência:</strong> Centenas de curiosos no bairro.</li>
                  <li><strong>Percepção do Cliente:</strong> Mão de obra barata (guerra de preços).</li>
                  <li><strong>Modelo de Cobrança:</strong> Chamados avulsos de R$ 50 a R$ 80.</li>
                </ul>
              </div>

              <div class="bg-[#0A0A0B] p-4 rounded-xl border border-emerald-500/30 space-y-2">
                <h3 class="text-xs font-bold text-emerald-400 uppercase tracking-wider">Técnico B: Especialista Posicionado</h3>
                <ul class="text-xs text-zinc-300 space-y-1.5 pl-4 list-disc text-justify">
                  <li><strong>Proposta:</strong> "Especialista em Redundância de Links MikroTik, VPNs e Cibersegurança para E-commerce."</li>
                  <li><strong>Concorrência:</strong> Poucos profissionais qualificados.</li>
                  <li><strong>Percepção do Cliente:</strong> Parceiro estratégico que evita prejuízos.</li>
                  <li><strong>Modelo de Cobrança:</strong> Contratos mensais (MSP) de R$ 600 a R$ 1.500.</li>
                </ul>
              </div>
            </div>

            <div class="space-y-2 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                4. Análise sobre Nicho e Guerra de Preços:
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                Por que o técnico generalista permanece preso na "guerra de preços" e como a escolha de um nicho específico em redes protege sua remuneração e atrai clientes corporativos?
              </p>
              <textarea 
                rows="3" 
                [(ngModel)]="answers.q4" 
                placeholder="Escreva sua análise..." 
                class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
            </div>
          </div>

          <!-- MÓDULO D -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO D: Construção da Proposta de Posicionamento Profissional</span>
            </h2>

            <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
              <strong>5. Mapeamento de Posicionamento de Carreira:</strong><br>
              Imagine que você está concluindo o curso técnico em redes e vai lançar seus serviços de consultoria ou buscar uma vaga de destaque no mercado corporativo. Escolha um <strong>nicho de redes</strong> (Ex: MikroTik Corporativo, Cabeamento Estruturado Certificado, Wi-Fi 6 de Alta Densidade para Hotéis/Eventos, Cibersegurança e Firewall pfSense) e preencha a declaração de posicionamento da sua equipe:
            </p>

            <div class="bg-[#F27D26]/10 border border-[#F27D26]/30 p-4 rounded-xl space-y-1">
              <h3 class="text-xs font-bold text-[#F27D26] flex items-center space-x-1">
                <span class="material-symbols-outlined text-sm">lightbulb</span>
                <span>💡 Estrutura da Proposta de Valor Profissional:</span>
              </h3>
              <p class="text-xs text-zinc-300 italic text-justify">
                "Eu ajudo <strong>[Público-Alvo / Nicho]</strong> a eliminar <strong>[Problema Técnico / Prejuízo Principal]</strong> através de <strong>[Sua Solução Especializada de Redes]</strong>, garantindo <strong>[Resultado Final / Valor Agregado]</strong>."
              </p>
            </div>

            <div class="grid grid-cols-1 gap-4 pt-2">
              <div class="space-y-1">
                <label class="block text-xs font-bold text-white">a) Público-Alvo / Nicho Escolhido:</label>
                <input type="text" [(ngModel)]="answers.q5_a" placeholder="Ex: Farmácias e Comércios de Médio Porte" class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none">
              </div>

              <div class="space-y-1">
                <label class="block text-xs font-bold text-white">b) Principal Dor Técnica ou Risco Financeiro que Você Resolve:</label>
                <input type="text" [(ngModel)]="answers.q5_b" placeholder="Ex: Queda de emissão de NF e parada de caixas" class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none">
              </div>

              <div class="space-y-1">
                <label class="block text-xs font-bold text-white">c) Sua Solução Especializada em Redes:</label>
                <input type="text" [(ngModel)]="answers.q5_c" placeholder="Ex: Implantação de MikroTik Failover Fibra + 4G" class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none">
              </div>

              <div class="space-y-1">
                <label class="block text-xs font-bold text-white">d) Declaração de Posicionamento Comercial Completa (Junte os itens a, b e c em uma frase de impacto):</label>
                <textarea rows="2" [(ngModel)]="answers.q5_d" placeholder="Escreva a declaração completa..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none"></textarea>
              </div>
            </div>
          </div>

          <!-- MÓDULO E: VÍDEO REFLEXIVO & INTRAEMPREENDEDORISMO -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO E: Estudo Audiovisual – O Intraempreendedorismo na Prática</span>
            </h2>

            <div class="bg-[#0A0A0B] p-5 rounded-xl border border-white/10 space-y-4">
              <h3 class="text-sm font-bold text-[#F27D26] flex items-center space-x-1.5">
                <span class="material-symbols-outlined text-base">smart_display</span>
                <span>🎥 Atividade em Vídeo: Empreendedorismo Corporativo na Carreira</span>
              </h3>
              
              <div class="space-y-3 text-xs sm:text-sm text-zinc-300">
                <div class="p-3 bg-white/5 rounded-xl border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <strong class="text-white">Vídeo 1:</strong> Intraempreendedorismo - Empreendedorismo Corporativo
                  </div>
                  <a 
                    href="https://www.youtube.com/watch?v=ue7S-WHV5DQ" 
                    target="_blank" 
                    class="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 font-bold transition-all text-xs w-fit">
                    <span class="material-symbols-outlined text-sm">open_in_new</span>
                    <span>Abrir Vídeo 1 no YouTube</span>
                  </a>
                </div>

                <div class="p-3 bg-white/5 rounded-xl border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <strong class="text-white">Vídeo 2:</strong> Intraempreendedorismo e Inovação na Prática
                  </div>
                  <a 
                    href="https://youtu.be/wATe8cLENYk" 
                    target="_blank" 
                    class="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 font-bold transition-all text-xs w-fit">
                    <span class="material-symbols-outlined text-sm">open_in_new</span>
                    <span>Abrir Vídeo 2 no YouTube</span>
                  </a>
                </div>
              </div>
            </div>

            <div class="space-y-2 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                <strong>1. Os 3 Pilares do Empreendedorismo Corporativo:</strong> O vídeo destaca que o intraempreendedorismo depende de 3 fatores simultâneos (Perfil do Colaborador, Cultura da Empresa e Papel da Liderança). Explique o que significa o colaborador ter <em>"atitude de questionar e propor melhorias"</em> em uma equipe de redes:
              </label>
              <textarea 
                rows="3" 
                [(ngModel)]="answers.q6_1" 
                placeholder="Escreva sua resposta aqui..." 
                class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
            </div>

            <div class="space-y-2 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                <strong>2. Riscos Controlados e Aprendizado:</strong> Segundo o vídeo, as empresas inovadoras precisam estar dispostas ao risco de errar de maneira controlada. Como um técnico de infraestrutura pode testar novas soluções em roteadores ou servidores sem derrubar a rede da empresa em horário comercial?
              </label>
              <textarea 
                rows="3" 
                [(ngModel)]="answers.q6_2" 
                placeholder="Escreva sua resposta aqui..." 
                class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
            </div>

            <div class="space-y-2 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                <strong>3. Postura Prática no Mercado de Trabalho:</strong> O vídeo conclui que empreendedor e intraempreendedor compartilham as mesmas ambições, mudando apenas se o negócio é próprio ou de outra pessoa. Qual atitude apresentada no vídeo você pretende praticar no seu primeiro emprego ou estágio em TIC?
              </label>
              <textarea 
                rows="3" 
                [(ngModel)]="answers.q6_3" 
                placeholder="Escreva sua resposta aqui..." 
                class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
            </div>
          </div>
        </div>
      }

      <!-- FULLSCREEN IMMERSIVE PRESENTATION OVERLAY MODAL (GAMMA APP / POWERPOINT STYLE) -->
      @if (isPresentationModalOpen()) {
        <div class="fixed inset-0 z-[100] bg-[#0A0A0B] backdrop-blur-2xl p-4 sm:p-8 flex flex-col justify-between select-none overflow-hidden h-screen w-screen max-h-screen max-w-screen animate-in fade-in zoom-in-95 duration-200">
          
          <!-- Top Bar inside Overlay -->
          <div class="max-w-6xl w-full mx-auto flex items-center justify-between pb-4 border-b border-white/10 text-white">
            <div class="flex items-center space-x-3">
              <span class="w-9 h-9 rounded-xl bg-[#F27D26]/20 border border-[#F27D26]/40 text-[#F27D26] font-bold flex items-center justify-center">
                <span class="material-symbols-outlined text-xl">slideshow</span>
              </span>
              <div>
                <h3 class="text-sm font-bold text-white tracking-wide">Aula 4: Tipos de Empreendedorismo e Posicionamento em Redes</h3>
              </div>
            </div>
          </div>

          <!-- Centered 16:9 Presentation Canvas Container -->
          <div class="max-w-6xl w-full mx-auto my-auto bg-[#121214] border border-white/15 rounded-3xl p-6 sm:p-12 shadow-2xl shadow-black/90 min-h-[460px] sm:min-h-[540px] flex flex-col justify-center relative overflow-hidden">
            <div class="absolute top-0 right-0 w-96 h-96 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>

            <!-- SLIDES CONTENT IN OVERLAY -->
            <div class="my-auto">
              <!-- SLIDE 1 -->
              @if (currentSlide() === 0) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">storefront</span>
                    <span>AULA 4 • TIPOS DE EMPREENDEDORISMO & MERCADO</span>
                  </span>

                  <div>
                    <h1 class="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                      Quem Você Quer Ser no Mercado de Redes?
                    </h1>
                    <p class="text-lg sm:text-xl text-zinc-400 font-medium mt-2">
                      Classificação dos modelos de empreendedorismo e posicionamento estratégico em TIC.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 sm:p-8 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                    <h3 class="text-xl font-bold text-white flex items-center space-x-2">
                      <span>🙋‍♂️</span>
                      <span>A Encruzilhada do João (Fim do Ano se Aproximando):</span>
                    </h3>
                    <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                      O João está concluindo o curso técnico em Redes. Com a experiência adquirida no projeto da <em>Logística Expressa</em>, surgiram vários caminhos: prestar serviços informais para o comércio local, abrir um MEI formal para emitir notas fiscais, prestar consultoria remota em nuvem ou aceitar uma vaga CLT como analista de suporte em um grande Provedor de Internet (ISP).
                    </p>
                  </div>

                  <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                    Ele percebeu que existem muitas formas de empreender além do modelo tradicional. Qual é a melhor escolha para o início de carreira?
                  </p>
                </div>
              }

              <!-- SLIDE 2 -->
              @if (currentSlide() === 1) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">analytics</span>
                    <span>PASSO 1 • PANORAMA DE MERCADO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Mitos e Verdades sobre Empreender
                  </h2>

                  <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                    Segundo dados e análises da <strong>Serasa Experian</strong>, empreendedorismo não se resume a abrir grandes empresas ou investir milhões:
                  </p>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-zinc-500 border-y border-r border-white/10 space-y-2 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-zinc-300 flex items-center space-x-2">
                        <span class="material-symbols-outlined text-zinc-400">domain_disabled</span>
                        <span>🏢 A Visão Tradicional (Engessada)</span>
                      </h3>
                      <p class="text-zinc-400 text-xs sm:text-sm leading-relaxed text-justify">
                        Acreditar que só empreende quem tem muito dinheiro para alugar escritório, comprar equipamentos caros e contratar dezenas de funcionários.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-2 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-[#F27D26] flex items-center space-x-2">
                        <span class="material-symbols-outlined text-[#F27D26]">rocket_launch</span>
                        <span>🚀 A Realidade do Mercado Atual</span>
                      </h3>
                      <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                        Empreender é a <strong>capacidade de identificar problemas, propor soluções inovadoras e gerar valor sustentável</strong> — seja de forma individual, digital, cooperativa ou dentro de uma grande corporação.
                      </p>
                    </div>
                  </div>

                  <div class="bg-[#F27D26]/10 rounded-2xl p-4 border border-[#F27D26]/40 text-center font-mono text-xs sm:text-sm font-bold text-[#F27D26] leading-relaxed shadow-lg">
                    NO BRASIL, MAIS DE 70% DAS EMPRESAS ATIVAS SÃO FORMADAS POR MICROEMPREENDEDORES INDIVIDUAIS (MEI) E PEQUENOS NEGÓCIOS!
                  </div>
                </div>
              }

              <!-- SLIDE 3 -->
              @if (currentSlide() === 2) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">explore</span>
                    <span>PASSO 2 • MOTIVAÇÃO INICIAL</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Empreendedorismo por Oportunidade vs. Necessidade
                  </h2>

                  <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                    A origem de qualquer iniciativa empreendedora parte de uma destas duas realidades:
                  </p>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <span class="px-2 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-400 font-bold uppercase font-mono">TIPO A</span>
                      <h3 class="text-base sm:text-lg font-bold text-amber-400">⚡ Por Necessidade (Reativo)</h3>
                      <ul class="text-zinc-300 text-xs sm:text-sm space-y-1.5 list-disc pl-4 text-justify">
                        <li>Surge pela falta de alternativas no mercado de trabalho formal ou desemprego.</li>
                        <li>Pouco ou nenhum planejamento prévio; foco em pagar as contas do mês.</li>
                        <li><strong class="text-amber-300">Ex em TIC:</strong> Passar cabos ou formatar computadores para vizinhos cobrando qualquer valor para gerar renda emergencial.</li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <span class="px-2 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-400 font-bold uppercase font-mono">TIPO B</span>
                      <h3 class="text-base sm:text-lg font-bold text-emerald-400">🎯 Por Oportunidade (Planejado)</h3>
                      <ul class="text-zinc-300 text-xs sm:text-sm space-y-1.5 list-disc pl-4 text-justify">
                        <li>Surge da identificação consciente de uma carência ou falha no mercado.</li>
                        <li>Envolve pesquisa de dados, cálculo de margem e proposta de valor clara.</li>
                        <li><strong class="text-emerald-300">Ex em TIC:</strong> Notar que os comércios locais perdem vendas por falta de link backup e criar planos de redundância gerenciada.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 4 -->
              @if (currentSlide() === 3) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">category</span>
                    <span>PASSO 3 • MODELOS DE NEGÓCIO (PARTE 1)</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Modelos de Empreendedorismo em TIC (Parte 1)
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2 shadow-xl">
                      <div class="text-3xl">👤</div>
                      <h3 class="text-sm font-bold text-white">1. Individual (MEI / Autônomo)</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        O profissional formaliza seu trabalho como Microempreendedor Individual para emitir Notas Fiscais e atender empresas como consultor ou prestador de serviços de redes.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-amber-500/30 space-y-2 shadow-xl">
                      <div class="text-3xl">⚠️</div>
                      <h3 class="text-sm font-bold text-amber-400">2. Informal</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Atuação sem registro formal ("bicos"). Embora comum no início, traz riscos: impede de fechar contratos corporativos com empresas médias que exigem emissão de nota.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-cyan-500/30 space-y-2 shadow-xl">
                      <div class="text-3xl">🌐</div>
                      <h3 class="text-sm font-bold text-cyan-400">3. Digital (Remoto / Nuvem)</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Prestação de suporte e gestão de infraestrutura 100% online (NOC em nuvem, configuração remota de roteadores MikroTik e servidores AWS/Azure para empresas de qualquer lugar).
                      </p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 5 -->
              @if (currentSlide() === 4) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">category</span>
                    <span>PASSO 4 • MODELOS DE NEGÓCIO (PARTE 2)</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Modelos de Empreendedorismo em TIC (Parte 2)
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-emerald-500/30 space-y-2 shadow-xl">
                      <div class="text-3xl">🏢</div>
                      <h3 class="text-sm font-bold text-emerald-400">4. Corporativo (Intraempreendedorismo)</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Empreender como funcionário dentro de uma empresa contratante, liderando melhorias, cortando desperdícios de links e criando novos serviços internos.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-purple-500/30 space-y-2 shadow-xl">
                      <div class="text-3xl">🤝</div>
                      <h3 class="text-sm font-bold text-purple-400">5. Cooperativo / Parcerias</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        União de técnicos autônomos ou pequenos provedores que compartilham custos de links dedicados (Redes Neutras / PTT) para competir com grandes multinacionais.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-teal-500/30 space-y-2 shadow-xl">
                      <div class="text-3xl">🌱</div>
                      <h3 class="text-sm font-bold text-teal-400">6. Social</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Iniciativas voltadas a resolver problemas comunitários, como projetos de inclusão digital que levam sinal de internet aberto para comunidades carentes e escolas rurais.
                      </p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 6 -->
              @if (currentSlide() === 5) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">badge</span>
                    <span>DESTAQUE • INTRAEMPREENDEDORISMO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Empreendendo com Carteira Assinada
                  </h2>

                  <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                    O <strong>Intraempreendedor</strong> é o profissional mais valorizado e promovido dentro das empresas de tecnologia:
                  </p>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-rose-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-rose-400">😴 O Funcionário Acomodado</h3>
                      <ul class="text-zinc-400 text-xs sm:text-sm space-y-1.5 list-disc pl-4 text-justify">
                        <li>Faz estritamente o que pediram no contrato e nada além.</li>
                        <li>Identifica uma falha grave de segurança no servidor e não avisa: <em>"Não é meu setor."</em></li>
                        <li>Passa anos na mesma função reclamando do salário.</li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-emerald-400">🚀 O Intraempreendedor em Redes</h3>
                      <ul class="text-zinc-300 text-xs sm:text-sm space-y-1.5 list-disc pl-4 text-justify">
                        <li>Automatiza tarefas repetitivas de backup com scripts em Linux/Python.</li>
                        <li>Propõe a reorganização de rotas BGP para economizar nos custos de tráfego do provedor.</li>
                        <li>Ganha destaque, assume cargos de liderança técnica e recebe bônus por inovação.</li>
                      </ul>
                    </div>
                  </div>

                  <div class="bg-indigo-950/40 border border-indigo-500/30 rounded-2xl p-4 text-xs sm:text-sm text-indigo-200 leading-relaxed text-justify">
                    💡 <strong>Cultura Open Organization (Caso Red Hat):</strong> Na Red Hat (gigante do Linux empresarial), colaboradores que sugerem e executam melhorias em infraestrutura têm voz ativa e crescem na liderança global da empresa.
                  </div>
                </div>
              }

              <!-- SLIDE 7 -->
              @if (currentSlide() === 6) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">crisis_alert</span>
                    <span>PASSO 5 • POSICIONAMENTO E PRECIFICAÇÃO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    A Armadilha do Técnico "Faz-Tudo"
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-rose-500/40 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-rose-400">O Técnico Genérico ("Faz-Tudo")</h3>
                      <div class="bg-rose-950/30 border border-rose-500/30 p-2.5 rounded text-center text-xs sm:text-sm font-mono text-rose-300">
                        "Formato PC, troco conector, arrumo impressora, passo cabo e mexo em celular."
                      </div>
                      <ul class="text-zinc-400 text-xs sm:text-sm space-y-1.5 list-disc pl-4 text-justify">
                        <li>Disputa clientes exclusivamente pelo <strong>menor preço</strong>.</li>
                        <li>O cliente o enxerga como mão de obra comum e pede desconto constantemente.</li>
                        <li>Ganha pouco por chamado avulso e trabalha até a exaustão.</li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-emerald-500/40 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-emerald-400">O Especialista Posicionado</h3>
                      <div class="bg-emerald-950/30 border border-emerald-500/30 p-2.5 rounded text-center text-xs sm:text-sm font-mono text-emerald-300">
                        "Especialista em Redundância de Links MikroTik e Cibersegurança para E-commerce."
                      </div>
                      <ul class="text-zinc-300 text-xs sm:text-sm space-y-1.5 list-disc pl-4 text-justify">
                        <li>Disputa contratos corporativos pelo <strong>valor gerado e autoridade</strong>.</li>
                        <li>O cliente paga mensalidades previsíveis de R$ 600 a R$ 1.500 sem reclamar.</li>
                        <li>Torna-se referência regional no seu segmento.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 8 -->
              @if (currentSlide() === 7) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">target</span>
                    <span>ESTRATÉGIA • NICHOS DE MERCADO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Nichos Estratégicos para Especialização
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-cyan-500/30 space-y-2 shadow-xl">
                      <span class="px-2 py-0.5 rounded text-[10px] bg-cyan-500/20 text-cyan-400 font-bold uppercase font-mono">NICHO 1</span>
                      <h3 class="text-sm font-bold text-white flex items-center space-x-1.5">
                        <span>🛡️ Cibersegurança & LGPD</span>
                      </h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Implantação de Firewalls (pfSense/Fortinet), VPNs seguras para Home Office e blindagem de dados contra vazamentos e ransomware.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-amber-500/30 space-y-2 shadow-xl">
                      <span class="px-2 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-400 font-bold uppercase font-mono">NICHO 2</span>
                      <h3 class="text-sm font-bold text-white flex items-center space-x-1.5">
                        <span>📶 Wi-Fi Corporativo & Eventos</span>
                      </h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Projetos de alta densidade sem quedas de sinal para hotéis, escolas, coworkings e eventos corporativos com roaming fluido.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-emerald-500/30 space-y-2 shadow-xl">
                      <span class="px-2 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-400 font-bold uppercase font-mono">NICHO 3</span>
                      <h3 class="text-sm font-bold text-white flex items-center space-x-1.5">
                        <span>🌾 Conectividade Rural & Agro (IoT)</span>
                      </h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Enlaces de longa distância via rádio e redes de sensores para galpões, fazendas e agroindústrias fora do alcance das operadoras.
                      </p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 9 -->
              @if (currentSlide() === 8) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">person_pin</span>
                    <span>CASO PRÁTICO • APLICAÇÃO REAL</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    O Posicionamento Estratégico do João
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-white/10 space-y-2 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-[#F27D26]">1. Formalização MEI + Digital:</h3>
                      <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                        Abriu seu <strong>MEI</strong> para emitir notas fiscais e montou um painel de monitoramento <strong>Digital em Nuvem</strong> (Zabbix/Telegram) para atender clientes sem precisar ficar preso fisicamente em um local.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-[#F27D26]/50 space-y-2 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-[#F27D26]">2. Declaração de Posicionamento Clara:</h3>
                      <p class="text-zinc-200 text-xs sm:text-sm leading-relaxed italic text-justify">
                        "Ajudo empresas de comércio e logística a eliminarem o risco de ficarem sem emitir Notas Fiscais através de soluções de Link Duplo MikroTik e monitoramento 24/7."
                      </p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 10 -->
              @if (currentSlide() === 9) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">formula</span>
                    <span>MÉTODO PRÁTICO • SUA MARCA PROFISSIONAL</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Como Criar seu Posicionamento em 3 Passos
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 text-center space-y-2 shadow-xl">
                      <div class="text-3xl">👥</div>
                      <h3 class="text-sm font-bold text-white">1. Para Quem? (Público)</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Qual nicho ou segmento você atende? (Ex: Mercados, Provedores, Escritórios de Contabilidade, Clínicas).
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-[#F27D26] text-center space-y-2 shadow-xl">
                      <div class="text-3xl">⚡</div>
                      <h3 class="text-sm font-bold text-[#F27D26]">2. Qual Dor? (Problema)</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Qual prejuízo você evita? (Ex: Quedas de internet na hora da venda, perda de dados por falta de backup).
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 text-center space-y-2 shadow-xl">
                      <div class="text-3xl">💎</div>
                      <h3 class="text-sm font-bold text-white">3. Qual Valor? (Resultado)</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Qual o benefício final? (Ex: Operação contínua 100% protegida, tranquilidade do empresário).
                      </p>
                    </div>
                  </div>

                  <div class="bg-[#F27D26]/10 rounded-2xl p-4 border border-[#F27D26]/40 text-center shadow-lg">
                    <p class="text-xs sm:text-base font-bold text-[#F27D26] italic">
                      "Eu ajudo [Público-Alvo] a resolver [Dor Principal] através de [Sua Solução Especializada de Redes], garantindo [Resultado Final de Valor]."
                    </p>
                  </div>
                </div>
              }

              <!-- SLIDE 11 -->
              @if (currentSlide() === 10) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">verified</span>
                    <span>SÍNTESE DA AULA</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    A Grande Lição do Dia
                  </h2>

                  <div class="bg-gradient-to-r from-[#F27D26]/20 via-[#F27D26]/10 to-transparent rounded-2xl p-6 sm:p-10 border border-[#F27D26]/50 text-center shadow-2xl">
                    <h3 class="text-xl sm:text-2xl lg:text-3xl font-extrabold text-[#F27D26] leading-relaxed">
                      "Não importa se você atuará como MEI, consultor em nuvem ou intraempreendedor em um grande provedor.<br><br>
                      O que define o valor do seu trabalho em redes é a sua clareza de posicionamento e o tamanho do problema que você resolve."
                    </h3>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-4 shadow-xl">
                    <h3 class="text-lg font-bold text-white flex items-center space-x-2">
                      <span>🚀</span>
                      <span>Próximo Passo:</span>
                    </h3>
                    <p class="text-zinc-300 text-xs sm:text-base leading-relaxed text-justify">
                      Abra a <strong>Folha de Atividades</strong>, analise os tipos de empreendedorismo aplicados ao setor de redes e construa a Proposta de Posicionamento Profissional da sua equipe!
                    </p>
                    <div>
                      <button 
                        type="button" 
                        (click)="closePresentationModal(); onSelectView('activity')" 
                        class="px-5 py-2.5 rounded-xl bg-[#F27D26] text-black font-bold text-xs sm:text-sm hover:bg-[#e06c17] transition-all flex items-center space-x-2 cursor-pointer shadow-lg shadow-[#F27D26]/20">
                        <span class="material-symbols-outlined text-base">assignment</span>
                        <span>Ir para Folha de Atividades Completa</span>
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Bottom Floating Control Dock (Gamma Style) Identical to Aula 3 -->
          <div class="max-w-3xl w-full mx-auto bg-[#121214]/95 backdrop-blur border border-white/15 rounded-2xl p-2.5 shadow-2xl flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:space-x-3">
            <button 
              type="button" 
              (click)="prevSlide()" 
              [disabled]="currentSlide() === 0"
              class="px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 text-xs font-semibold text-white border border-white/10 transition-all flex items-center space-x-1 cursor-pointer shrink-0">
              <span class="material-symbols-outlined text-base">chevron_left</span>
              <span class="hidden sm:inline">Anterior</span>
            </button>

            <!-- Interactive Slide Jump Buttons (Pílulas Numeradas) -->
            <div class="flex items-center justify-center gap-1 flex-wrap">
              @for (slideNum of slideNumbers; track slideNum) {
                <button 
                  type="button" 
                  (click)="goToSlide(slideNum)"
                  [class]="currentSlide() === slideNum 
                    ? 'bg-[#F27D26] text-black font-bold scale-110 shadow-md shadow-[#F27D26]/30' 
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'"
                  class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg text-[11px] font-mono transition-all flex items-center justify-center cursor-pointer">
                  {{ slideNum + 1 }}
                </button>
              }
            </div>

            <button 
              type="button" 
              (click)="nextSlide()" 
              [disabled]="currentSlide() === totalSlides - 1"
              class="px-3.5 py-2 rounded-xl bg-[#F27D26] hover:bg-[#e06c17] disabled:opacity-30 disabled:hover:bg-[#F27D26] text-xs font-bold text-black transition-all flex items-center space-x-1 cursor-pointer shrink-0">
              <span class="hidden sm:inline">Próximo</span>
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      }
    </section>
  `
})
export class Aula4SectionComponent {
  navigateBack = output<void>();

  activeView = signal<'slides' | 'activity'>('slides');
  currentSlide = signal<number>(0);
  totalSlides = 11;
  slideNumbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  isPresentationModalOpen = signal<boolean>(false);

  answers = {
    q1_1: '',
    q1_2: '',
    q1_3: '',
    q1_4: '',
    q1_5: '',
    q2: '',
    q3: '',
    q4: '',
    q5_a: '',
    q5_b: '',
    q5_c: '',
    q5_d: '',
    q6_1: '',
    q6_2: '',
    q6_3: ''
  };

  onSelectView(view: 'slides' | 'activity') {
    this.activeView.set(view);
    if (view === 'slides') {
      this.openPresentationModal();
    }
  }

  nextSlide() {
    if (this.currentSlide() < this.totalSlides - 1) {
      this.currentSlide.update(c => c + 1);
    }
  }

  prevSlide() {
    if (this.currentSlide() > 0) {
      this.currentSlide.update(c => c - 1);
    }
  }

  goToSlide(slideIndex: number) {
    if (slideIndex >= 0 && slideIndex < this.totalSlides) {
      this.currentSlide.set(slideIndex);
    }
  }

  openPresentationModal() {
    this.isPresentationModalOpen.set(true);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    }
  }

  closePresentationModal() {
    this.isPresentationModalOpen.set(false);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    }
  }

  printActivitySheet() {
    if (typeof window !== 'undefined') {
      window.print();
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (this.isPresentationModalOpen() || this.activeView() === 'slides') {
      if (event.key === 'ArrowRight' || event.key === ' ') {
        this.nextSlide();
      } else if (event.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (event.key === 'Escape') {
        this.closePresentationModal();
      }
    }
  }
}
